import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type ReportRow,
  type ReportRowReport,
  type ReportRowState,
  type ReportsByMatchPayload,
} from "../lib/api";
import { routes } from "../routes";
import TournamentBottomNav from "../components/TournamentBottomNav";
import GuideHelpButton from "../components/GuideHelpButton";
import ResultSetCapture, { type ResultSetCapturePayload } from "../components/ResultSetCapture";
import { jornadaRangeText } from "../lib/jornadaDates";
import { isPickleballSport } from "../lib/scoreParsing";
import { scoreTextFromWinner } from "../lib/scoreFromWinner";
import { reportWarnings, type CapturePlayer } from "../lib/resultCapture";

/** Resultados reportados POR PARTIDO (maqueta Admin-cola aprobada por
 * Eduardo 2026-10-06, inspirada en su sistema anterior): un renglón por
 * partido con lo que reportó cada jugador, contadores, chips de jornada y
 * grupos. «Editar» abre la MISMA captura por sets que usan los jugadores.
 * Todos los marcadores se leen desde quien ganó. */

const STATE_LABELS: Record<ReportRowState, string> = {
  auto_approved: "Coinciden · aprobado solo",
  approved: "Aprobado",
  matching: "Coinciden · por aprobar",
  mismatch: "No coinciden",
  one_report: "1 reporte",
  no_report: "Sin reporte",
};

const ALL = "all";

function jornadaKey(row: ReportRow): string {
  if (row.match.round_number != null) return `j${row.match.round_number}`;
  return `r:${row.match.round_display_label ?? row.match.round_label ?? "Sin jornada"}`;
}

function jornadaChipLabel(key: string, rows: ReportRow[]): string {
  if (key.startsWith("j")) return key.slice(1);
  const row = rows.find((r) => jornadaKey(r) === key);
  return row?.match.round_display_label ?? row?.match.round_label ?? "Sin jornada";
}

function jornadaTitle(key: string, rows: ReportRow[]): string {
  if (key.startsWith("j")) return `Jornada ${key.slice(1)}`;
  return jornadaChipLabel(key, rows);
}

function firstName(name: string | null | undefined): string {
  const clean = (name ?? "").trim();
  if (!clean) return "—";
  if (clean.includes("/")) return clean;
  return clean.split(/\s+/)[0];
}

function timeAgo(iso: string | null, nowMs: number): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const minutes = Math.max(0, Math.round((nowMs - then) / 60000));
  if (minutes < 1) return "hace un momento";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? "hace 1 día" : `hace ${days} días`;
}

const EXCEPTION_STATUSES = new Set(["walkover", "defaulted", "retired", "no_show"]);

type ClosureType = "normal" | "walkover" | "defaulted" | "retired";

const CLOSURE_OPTIONS: { value: ClosureType; label: string }[] = [
  { value: "normal", label: "Resultado normal" },
  { value: "walkover", label: "W.O. / no se presentó" },
  { value: "defaulted", label: "DEF (default)" },
  { value: "retired", label: "RET (retiro)" },
];

/** Cierre vigente del partido (2026-10-19): W.O., DEF o RET se editan aquí
 * mismo con la misma ruta que usa el Calendario (/walkover). */
function closureOf(row: ReportRow): ClosureType {
  if (row.state !== "approved" && row.state !== "auto_approved") return "normal";
  const status = row.match.status;
  if (status === "walkover" || status === "no_show") return "walkover";
  if (status === "defaulted" || status === "retired") return status;
  if (!EXCEPTION_STATUSES.has(status)) {
    const raw = row.match.score_text ?? "";
    if (/\bRET\b/i.test(raw)) return "retired";
    if (/\bDEF\b/i.test(raw)) return "defaulted";
    if (/\bW\.?\s*O\b/i.test(raw)) return "walkover";
  }
  return "normal";
}

/** Marcador parcial (sin la etiqueta RET/DEF/W.O.) para precargar el editor. */
function partialScore(raw: string | null): string {
  return (raw ?? "").replace(/\b(RET|DEF)\b\.?/gi, "").replace(/\bW\.?\s*O\b\.?/gi, "").replace(/\s+/g, " ").trim();
}

const EXCEPTION_LABELS: Record<string, string> = {
  walkover: "W.O.",
  defaulted: "DEF",
  retired: "RET",
  no_show: "No se presentó",
};

/** Resultado oficial vigente del partido (2026-10-19): «DEF · Ganó …»,
 * «RET · 6-3 2-1 · Ganó …» o «6-3 6-4 · Ganó …». null si aún no hay. */
function officialResult(row: ReportRow): { kind: string | null; score: string; winner: string | null } | null {
  const match = row.match;
  if (!(match.status in EXCEPTION_LABELS) && match.status !== "completed") return null;
  const raw = match.score_text ?? "";
  let kind = EXCEPTION_LABELS[match.status] ?? null;
  if (!kind) {
    if (/\bRET\b/i.test(raw)) kind = "RET";
    else if (/\bDEF\b/i.test(raw)) kind = "DEF";
    else if (/\bW\.?\s*O\b\.?/i.test(raw)) kind = "W.O.";
  }
  const winnerId = match.winner_participant_id;
  const players = rowPlayers(row);
  const winner = players.find((p) => p.id === winnerId) ?? null;
  const sets = raw.replace(/\b(RET|DEF)\b\.?/gi, "").replace(/\bW\.?\s*O\b\.?/gi, "").replace(/\s+/g, " ").trim();
  const score = scoreTextFromWinner(sets, winner ? winner.id === players[0].id : null);
  return { kind, score, winner: winner ? winner.name : null };
}

function officialResultText(row: ReportRow): string | null {
  const result = officialResult(row);
  if (!result) return null;
  return [result.kind, result.score || null, result.winner ? `Ganó ${result.winner}` : "Sin ganador"]
    .filter(Boolean)
    .join(" · ");
}

function rowPlayers(row: ReportRow): [CapturePlayer, CapturePlayer] {
  return [
    { id: row.match.player1_participant_id, name: row.match.player1?.display_name ?? `Jugador #${row.match.player1_participant_id}` },
    { id: row.match.player2_participant_id, name: row.match.player2?.display_name ?? `Jugador #${row.match.player2_participant_id}` },
  ];
}

/** El reporte que precarga «Editar» / «Aprobar así»: el pendiente más
 * reciente (o el único). */
function pendingReport(row: ReportRow): ReportRowReport | null {
  const pending = [row.player1_report, row.player2_report, row.referee_report ?? null].filter(
    (r): r is ReportRowReport => r !== null && r.status === "pending",
  );
  if (!pending.length) return null;
  return [...pending].sort((a, b) => (b.updated_at ?? b.created_at ?? "").localeCompare(a.updated_at ?? a.created_at ?? ""))[0];
}

function ReportCell({
  report,
  players,
  pickleball,
  nowMs,
}: {
  report: ReportRowReport | null;
  players: [CapturePlayer, CapturePlayer];
  pickleball: boolean;
  nowMs: number;
}) {
  if (!report) return <span className="muted">Sin reporte</span>;
  const resolved = report.resolved_by_official_result === true;
  const winner = players.find((p) => p.id === report.winner_participant_id) ?? null;
  const score = scoreTextFromWinner(report.score_text, winner ? winner.id === players[0].id : null);
  const warnings = report.status === "pending" ? reportWarnings(report.score_text, report.winner_participant_id, players, pickleball) : [];
  const ago = timeAgo(report.updated_at ?? report.created_at, nowMs);
  return (
    <div className="report-cell">
      <div className="report-cell__winner">{winner ? `Ganó ${firstName(winner.name)}` : "Sin ganador"}</div>
      <div className="report-cell__score">{score || "—"}</div>
      {warnings.map((warning) => (
        <div key={warning} className="report-cell__warning">
          ⚠ {warning}
        </div>
      ))}
      {ago ? <div className="report-cell__ago muted">{ago}</div> : null}
      {resolved ? <div className="report-cell__resolved">Resuelto por el resultado oficial</div> : null}
      {report.notes ? <div className="report-cell__ago muted">Nota: {report.notes}</div> : null}
    </div>
  );
}

function StateBadge({ row, warnings }: { row: ReportRow; warnings: boolean }) {
  const label = row.state === "one_report" && warnings ? "1 reporte · revisar" : STATE_LABELS[row.state];
  return (
    <span className={`report-state report-state--${row.state}`}>
      {row.state === "auto_approved" ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      ) : null}
      {label}
    </span>
  );
}

export default function AdminTournamentResultReportsPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId]);
  const { token } = useAuth();

  const [data, setData] = useState<ReportsByMatchPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [editingMatchId, setEditingMatchId] = useState<number | null>(null);
  const [busyMatchId, setBusyMatchId] = useState<number | null>(null);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [closure, setClosure] = useState<ClosureType>("normal");
  const [exceptionWinner, setExceptionWinner] = useState<number | null>(null);
  const [exceptionScore, setExceptionScore] = useState("");
  const nowMs = Date.now();

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    try {
      const response = await api.resultReports.listByMatch(token, tournamentId);
      setData(response.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No pudimos cargar los reportes." : "Error de red. Intenta otra vez.");
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const pickleball = isPickleballSport(data?.settings?.sport);

  const jornadaKeys = useMemo(() => {
    const keys: string[] = [];
    for (const row of rows) {
      const key = jornadaKey(row);
      if (!keys.includes(key)) keys.push(key);
    }
    const numbered = keys.filter((k) => k.startsWith("j")).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
    return [...numbered, ...keys.filter((k) => !k.startsWith("j"))];
  }, [rows]);

  // Abre en la primera jornada con reportes por revisar; si no, en la primera
  // con partidos sin reporte.
  const defaultKey = useMemo(() => {
    const pendingStates: ReportRowState[] = ["mismatch", "one_report", "matching"];
    const withPending = jornadaKeys.find((key) => rows.some((r) => jornadaKey(r) === key && pendingStates.includes(r.state)));
    if (withPending) return withPending;
    const withMissing = jornadaKeys.find((key) => rows.some((r) => jornadaKey(r) === key && r.state === "no_report"));
    return withMissing ?? jornadaKeys[0] ?? ALL;
  }, [jornadaKeys, rows]);
  const current = selected && (selected === ALL || jornadaKeys.includes(selected)) ? selected : defaultKey;
  const visibleRows = current === ALL ? rows : rows.filter((row) => jornadaKey(row) === current);

  const counts = useMemo(() => {
    const count = (states: ReportRowState[]) => visibleRows.filter((row) => states.includes(row.state)).length;
    return {
      total: visibleRows.length,
      noReport: count(["no_report"]),
      oneReport: count(["one_report"]),
      matching: count(["auto_approved", "matching"]),
      mismatch: count(["mismatch"]),
    };
  }, [visibleRows]);

  async function toggleAuto(checked: boolean) {
    if (!token || !data) return;
    setTogglingAuto(true);
    setError(null);
    setMessage(null);
    try {
      await api.tournaments.updateSettings(token, tournamentId, { auto_approve_matching_reports: checked });
      setData({ ...data, settings: { ...data.settings, auto_approve_matching_reports: checked } });
      setMessage(
        checked
          ? "Listo: cuando los dos jugadores reporten lo mismo, el resultado se aprueba solo."
          : "Listo: todos los reportes esperan a que los apruebes.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo cambiar el ajuste." : "Error de red. Intenta otra vez.");
    } finally {
      setTogglingAuto(false);
    }
  }

  async function approve(row: ReportRow, payload: { score_text: string | null; winner_participant_id: number | null }) {
    if (!token) return;
    setBusyMatchId(row.match.id);
    setError(null);
    setMessage(null);
    try {
      await api.resultReports.approveMatch(token, tournamentId, row.match.id, payload);
      setMessage("Resultado aprobado y aplicado al partido.");
      setEditingMatchId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo aprobar el resultado." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyMatchId(null);
    }
  }

  function openEditor(row: ReportRow) {
    const kind = closureOf(row);
    setClosure(kind);
    setExceptionWinner(kind === "normal" ? null : row.match.winner_participant_id ?? null);
    setExceptionScore(kind === "normal" ? "" : partialScore(row.match.score_text));
    setEditingMatchId(row.match.id);
  }

  async function saveException(row: ReportRow) {
    if (!token || closure === "normal") return;
    if (!exceptionWinner) {
      setError("Selecciona al ganador: un W.O., default o retiro otorga la victoria al rival.");
      return;
    }
    setBusyMatchId(row.match.id);
    setError(null);
    setMessage(null);
    try {
      await api.matches.walkover(token, tournamentId, row.match.id, {
        winner_participant_id: exceptionWinner,
        exception_type: closure,
        ...(closure === "walkover" ? {} : { score_text: exceptionScore.trim() || null }),
      });
      setMessage("Resultado guardado y aplicado al partido.");
      setEditingMatchId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo guardar el resultado." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyMatchId(null);
    }
  }

  /** Un partido cerrado por W.O./DEF/RET que pasa a resultado normal: misma
   * ruta que el Calendario (/result); «aprobar» lo rechaza por ser excepción. */
  async function saveNormalOverException(row: ReportRow, payload: ResultSetCapturePayload) {
    if (!token) return;
    setBusyMatchId(row.match.id);
    setError(null);
    setMessage(null);
    try {
      await api.matches.recordResult(token, tournamentId, row.match.id, {
        score_text: payload.score_text,
        winner_participant_id: payload.winner_participant_id,
      });
      setMessage("Resultado guardado y aplicado al partido.");
      setEditingMatchId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo guardar el resultado." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyMatchId(null);
    }
  }

  async function askAgain(row: ReportRow) {
    if (!token) return;
    if (
      !(await askConfirm("¿Pedir a los dos jugadores que reporten de nuevo? Sus reportes se rechazan y les llega un aviso.", {
        acceptLabel: "Sí, pedir que reporten de nuevo",
        tone: "danger",
      }))
    ) {
      return;
    }
    setBusyMatchId(row.match.id);
    setError(null);
    setMessage(null);
    try {
      await api.resultReports.rejectMatch(token, tournamentId, row.match.id, {});
      setMessage("Listo: les pedimos a los jugadores que reporten de nuevo.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo rechazar." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyMatchId(null);
    }
  }

  function renderActions(row: ReportRow) {
    const busy = busyMatchId === row.match.id;
    const pending = pendingReport(row);
    const edit = (label: string) => (
      <button type="button" className="btn btn--compact" disabled={busy} onClick={() => openEditor(row)}>
        {label}
      </button>
    );
    const approveAsIs = pending ? (
      <button
        type="button"
        className="btn btn--ghost btn--compact"
        disabled={busy}
        onClick={() => void approve(row, { score_text: pending.score_text, winner_participant_id: pending.winner_participant_id })}
      >
        Aprobar así
      </button>
    ) : null;
    switch (row.state) {
      case "mismatch":
        return (
          <div className="table-actions">
            {edit("Editar y aprobar")}
            <button type="button" className="btn btn--ghost btn--compact" disabled={busy} onClick={() => void askAgain(row)}>
              Pedir que reporten de nuevo
            </button>
          </div>
        );
      case "one_report":
      case "matching":
        return (
          <div className="table-actions">
            {edit("Editar")}
            {approveAsIs}
          </div>
        );
      case "no_report":
        return <div className="table-actions">{edit("Capturar")}</div>;
      default:
        return <div className="table-actions">{edit("Editar")}</div>;
    }
  }

  function renderEditor(row: ReportRow) {
    const players = rowPlayers(row);
    const pending = pendingReport(row);
    const fromMatch = row.state === "approved" || row.state === "auto_approved";
    const source = fromMatch
      ? { score: partialScore(row.match.score_text) || null, winner: row.match.winner_participant_id }
      : pending
        ? { score: pending.score_text, winner: pending.winner_participant_id }
        : { score: null, winner: null };
    return (
      <tr className="report-table__editor">
        <td colSpan={5}>
          {/* Envoltura sticky: a 390px la tabla se desliza de lado y el editor
              se queda dentro de la pantalla. */}
          <div className="report-table__editor-inner">
          <label className="report-closure" htmlFor={`admin-reporte-${row.match.id}-cierre`}>
            <span>Tipo de cierre</span>
            <select
              id={`admin-reporte-${row.match.id}-cierre`}
              data-testid="report-closure-type"
              value={closure}
              onChange={(event) => {
                const next = event.target.value as ClosureType;
                setClosure(next);
                if (next !== "normal" && exceptionWinner === null) setExceptionWinner(row.match.winner_participant_id ?? null);
              }}
            >
              {CLOSURE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {closure !== "normal" ? (
            <form
              className="report-closure__form"
              onSubmit={(event) => {
                event.preventDefault();
                void saveException(row);
              }}
            >
              <fieldset className="report-closure__winner">
                <legend>Ganador</legend>
                {players.map((player) => (
                  <label key={player.id}>
                    <input
                      type="radio"
                      name={`admin-reporte-${row.match.id}-ganador`}
                      checked={exceptionWinner === player.id}
                      onChange={() => setExceptionWinner(player.id)}
                    />{" "}
                    {player.name}
                  </label>
                ))}
              </fieldset>
              {closure !== "walkover" ? (
                <label className="report-closure__score">
                  <span>Marcador al momento (opcional)</span>
                  <input
                    type="text"
                    value={exceptionScore}
                    placeholder="6-3 2-1"
                    onChange={(event) => setExceptionScore(event.target.value)}
                  />
                </label>
              ) : null}
              <div className="table-actions">
                <button type="submit" className="btn btn--compact" disabled={busyMatchId === row.match.id}>
                  Guardar
                </button>
                <button type="button" className="btn btn--ghost btn--compact" onClick={() => setEditingMatchId(null)}>
                  Cancelar
                </button>
              </div>
            </form>
          ) : (
          <ResultSetCapture
            idPrefix={`admin-reporte-${row.match.id}`}
            player1={players[0]}
            player2={players[1]}
            pickleball={pickleball}
            initialScoreText={source.score}
            initialWinnerId={source.winner}
            busy={busyMatchId === row.match.id}
            showNotes={false}
            submitLabel="Guardar y aprobar"
            confirmLabel="Guardar y aprobar"
            confirmNote="Se aplica al partido y se actualizan las posiciones."
            onSubmit={(payload: ResultSetCapturePayload) =>
              closureOf(row) !== "normal"
                ? saveNormalOverException(row, payload)
                : approve(row, { score_text: payload.score_text, winner_participant_id: payload.winner_participant_id })
            }
            onCancel={() => setEditingMatchId(null)}
          />
          )}
          </div>
        </td>
      </tr>
    );
  }

  const sectionKeys = current === ALL ? jornadaKeys : [current];
  const divisionCount = new Set(rows.map((row) => row.match.division_id)).size;

  function sectionRange(key: string, sectionRows: ReportRow[]): string | null {
    if (!key.startsWith("j") || !data) return null;
    const number = Number(key.slice(1));
    const divisionIds = new Set(sectionRows.map((row) => row.match.division_id));
    const windows = data.saved_windows.filter((w) => w.round_number === number && divisionIds.has(w.division_id));
    const first = windows[0];
    if (!first) return null;
    const same = windows.every((w) => w.starts_on === first.starts_on && w.ends_on === first.ends_on);
    return same ? jornadaRangeText(first.starts_on, first.ends_on) : null;
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">
              <span className="hint-label">
                Resultados reportados
                <GuideHelpButton />
              </span>
            </h1>
            <p className="card__subtitle">
              {data ? `${data.tournament.name} · ` : ""}Un renglón por partido, con lo que reportó cada jugador.
            </p>
          </div>
          <div className="form__actions">
            <Link className="btn btn--ghost" to={routes.adminTournamentDetail(tournamentId)}>
              ← Detalle del torneo
            </Link>
            <Link className="btn btn--ghost" to={routes.adminTournamentSchedule(tournamentId)}>
              Calendario
            </Link>
          </div>
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}

        <div className="report-counters" aria-label="Resumen de reportes">
          <div className="report-counter">
            <div className="report-counter__num">{counts.total}</div>
            <div className="report-counter__label">{current === ALL ? "Partidos" : "Partidos de la jornada"}</div>
          </div>
          <div className="report-counter">
            <div className="report-counter__num">{counts.noReport}</div>
            <div className="report-counter__label">Sin reporte</div>
          </div>
          <div className="report-counter">
            <div className="report-counter__num">{counts.oneReport}</div>
            <div className="report-counter__label">1 reporte</div>
          </div>
          <div className="report-counter">
            <div className="report-counter__num">{counts.matching}</div>
            <div className="report-counter__label">Coinciden</div>
          </div>
          <div className={`report-counter${counts.mismatch ? " is-alert" : ""}`}>
            <div className="report-counter__num">{counts.mismatch}</div>
            <div className="report-counter__label">No coinciden</div>
          </div>
        </div>

        <div className="report-toolbar">
          {jornadaKeys.length > 0 ? (
            <nav aria-label="Jornadas" className="report-chips">
              <span className="muted">Jornada:</span>
              {[...jornadaKeys, ALL].map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`report-chip${current === key ? " is-current" : ""}`}
                  aria-current={current === key ? "page" : undefined}
                  onClick={() => {
                    setSelected(key);
                    setEditingMatchId(null);
                  }}
                >
                  {key === ALL ? "Todas" : jornadaChipLabel(key, rows)}
                </button>
              ))}
            </nav>
          ) : null}
          <label className="report-auto">
            <input
              type="checkbox"
              checked={data?.settings?.auto_approve_matching_reports !== false}
              disabled={!data || togglingAuto}
              onChange={(e) => void toggleAuto(e.target.checked)}
            />
            Aprobar solo cuando los dos reportan lo mismo
          </label>
        </div>
      </section>

      {loading && !data ? (
        <section className="card">
          <p className="muted">Cargando…</p>
        </section>
      ) : rows.length === 0 ? (
        <section className="card">
          <p className="muted">Todavía no hay partidos con los dos rivales.</p>
        </section>
      ) : (
        sectionKeys.map((key) => {
          const sectionRows = rows.filter((row) => jornadaKey(row) === key);
          const range = sectionRange(key, sectionRows);
          const groups: Array<{ key: string; label: string; groupId: number | null; rows: ReportRow[] }> = [];
          for (const row of sectionRows) {
            const groupKey = `${row.match.division_id ?? ""}|${row.match.group_id ?? ""}`;
            let group = groups.find((g) => g.key === groupKey);
            if (!group) {
              const parts = [row.match.group_name ? row.match.group_name.toUpperCase() : null];
              if (divisionCount > 1 && row.match.division_name) parts.push(row.match.division_name.toUpperCase());
              group = { key: groupKey, label: parts.filter(Boolean).join(" · "), groupId: row.match.group_id, rows: [] };
              groups.push(group);
            }
            group.rows.push(row);
          }
          const number = key.startsWith("j") ? Number(key.slice(1)) : null;
          return (
            <section key={key} className="card" aria-labelledby={`jornada-${key}`}>
              <h2 id={`jornada-${key}`} className="card__title">
                {jornadaTitle(key, rows)}
                {range ? <span className="muted"> · {range}</span> : null}
              </h2>
              {groups.map((group) => {
                const rests =
                  number !== null && data
                    ? data.rest_by_round.filter((rest) => rest.round_number === number && rest.group_id === group.groupId)
                    : [];
                return (
                  <div key={group.key} className="report-group">
                    {group.label ? <span className="report-group__label">{group.label}</span> : null}
                    <div className="responsive-table-wrap">
                      <table className="data-table dashboard-table report-table">
                        <thead>
                          <tr>
                            <th>Partido</th>
                            <th>Reporte del jugador 1</th>
                            <th>Reporte del jugador 2</th>
                            <th>Estado</th>
                            <th>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row) => {
                            const players = rowPlayers(row);
                            const winnerId = row.match.winner_participant_id;
                            const pending = pendingReport(row);
                            const hasWarnings =
                              row.state === "one_report" && pending
                                ? reportWarnings(pending.score_text, pending.winner_participant_id, players, pickleball).length > 0
                                : false;
                            return (
                              <Fragment key={row.match.id}>
                                <tr className={row.state === "mismatch" ? "report-table__row is-mismatch" : "report-table__row"}>
                                  <td className="data-table__cell--name">
                                    <div>{winnerId === players[0].id ? <strong>{players[0].name}</strong> : players[0].name}</div>
                                    <div>
                                      vs {winnerId === players[1].id ? <strong>{players[1].name}</strong> : players[1].name}
                                    </div>
                                    {officialResultText(row) ? (
                                      <div className="report-official" data-official-result={row.match.id}>
                                        <span className="report-official__label">Resultado oficial</span>
                                        <span className="report-official__text">{officialResultText(row)}</span>
                                      </div>
                                    ) : null}
                                    {row.referee_report ? (
                                      <div className="report-referee" data-referee-report={row.match.id}>
                                        <span className="report-referee__label">
                                          Árbitro{row.referee_report.reported_by_user_name ? `: ${row.referee_report.reported_by_user_name}` : ""}
                                        </span>
                                        <ReportCell report={row.referee_report} players={players} pickleball={pickleball} nowMs={nowMs} />
                                      </div>
                                    ) : null}
                                  </td>
                                  <td data-label="Reporte del jugador 1">
                                    <ReportCell report={row.player1_report} players={players} pickleball={pickleball} nowMs={nowMs} />
                                  </td>
                                  <td data-label="Reporte del jugador 2">
                                    <ReportCell report={row.player2_report} players={players} pickleball={pickleball} nowMs={nowMs} />
                                  </td>
                                  <td data-label="Estado">
                                    <StateBadge row={row} warnings={hasWarnings} />
                                  </td>
                                  <td data-label="Acción">{renderActions(row)}</td>
                                </tr>
                                {editingMatchId === row.match.id ? renderEditor(row) : null}
                              </Fragment>
                            );
                          })}
                          {rests.flatMap((rest) =>
                            rest.participants.map((participant) => (
                              <tr key={`descansa-${rest.group_id}-${participant.id}`} className="report-table__rest">
                                <td colSpan={5}>{participant.name} descansa esta jornada.</td>
                              </tr>
                            )),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
              <p className="muted report-footnote">
                «Editar» abre la misma captura por sets que usan los jugadores. Todos los marcadores se leen desde quien ganó.
              </p>
            </section>
          );
        })
      )}
      <TournamentBottomNav tournamentId={tournamentId} />
    </>
  );
}
